import { create } from 'zustand';
import { activateMailOrder } from '../services/mailorderApi';
import { getAccountInfo } from '../services/accountApi';
import type { NormalizedApiError } from '../services/httpClient';
import { getSessionUser, setSessionUser, type SessionUser } from '../utils/session';
import type { EProtectSuccessResponse } from '../types/runtime';

// ─── Domain types ─────────────────────────────────────────────────────────────

export interface SelectedUser {
  linkToken: string;
  displayName: string;
  dob: string;
}

export interface AllergyProfile {
  none: boolean;
  codeine: boolean;
  sulfa: boolean;
  aspirin: boolean;
  penicillin: boolean;
  other: string;
}

export interface SeverityProfile {
  none: boolean;
  mild: boolean;
  moderate: boolean;
  severe: boolean;
  intolerance: boolean;
  anaphylaxis: boolean;
}

export interface ConditionProfile {
  none: boolean;
  thyroid: boolean;
  hbp: boolean;
  diabetes: boolean;
  glaucoma: boolean;
  heart: boolean;
  intestinal: boolean;
  lung: boolean;
  other: string;
}

export interface DemographicsPhone {
  areaCode: string;
  phoneNumber: string;
  extension: string;
}

export interface DemographicsAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Demographics {
  phone: DemographicsPhone;
  address: DemographicsAddress;
}

// ─── Default values ───────────────────────────────────────────────────────────

const DEFAULT_ALLERGIES: AllergyProfile = {
  none: true,
  codeine: false,
  sulfa: false,
  aspirin: false,
  penicillin: false,
  other: '',
};

const DEFAULT_SEVERITY: SeverityProfile = {
  none: true,
  mild: false,
  moderate: false,
  severe: false,
  intolerance: false,
  anaphylaxis: false,
};

const DEFAULT_CONDITIONS: ConditionProfile = {
  none: true,
  thyroid: false,
  hbp: false,
  diabetes: false,
  glaucoma: false,
  heart: false,
  intestinal: false,
  lung: false,
  other: '',
};

const DEFAULT_DEMOGRAPHICS: Demographics = {
  phone: { areaCode: '', phoneNumber: '', extension: '' },
  address: { street1: '', street2: '', city: '', state: '', zipCode: '' },
};

// ─── Store interface ──────────────────────────────────────────────────────────

export interface MailOrderState {
  // Navigation
  currentStep: number; // 1–5
  // Step 1
  selectedUser: SelectedUser | null;
  // Step 2
  allergies: AllergyProfile;
  severity: SeverityProfile;
  conditions: ConditionProfile;
  // Step 3
  eprotectResponse: EProtectSuccessResponse | null;
  paymentType: 'card' | 'check';
  cardName: string;
  cardExpMonth: string;
  cardExpYear: string;
  cardLast4: string;
  nyDisclaimerAccepted: boolean;
  // Step 4
  demographics: Demographics;
  // Step 5
  acknowledgeTerms: boolean;
  // Async state
  isSubmitting: boolean;
  submitError: string | null;
  submitSuccess: boolean;

  // Actions — navigation
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  // Actions — user selection
  setSelectedUser: (user: SelectedUser) => void;
  // Actions — health profile
  updateAllergy: (
    key: keyof Omit<AllergyProfile, 'none' | 'other'> | 'other',
    value: boolean | string
  ) => void;
  setAllergyNone: () => void;
  updateSeverity: (key: keyof Omit<SeverityProfile, 'none'>, value: boolean) => void;
  setSeverityNone: () => void;
  updateCondition: (
    key: keyof Omit<ConditionProfile, 'none' | 'other'> | 'other',
    value: boolean | string
  ) => void;
  setConditionNone: () => void;
  // Actions — payment
  setEprotectResponse: (response: EProtectSuccessResponse) => void;
  setPaymentType: (type: 'card' | 'check') => void;
  setCardName: (name: string) => void;
  setCardExpMonth: (month: string) => void;
  setCardExpYear: (year: string) => void;
  setCardLast4: (last4: string) => void;
  setNyDisclaimerAccepted: (val: boolean) => void;
  // Actions — demographics
  updateDemographicsPhone: (field: keyof DemographicsPhone, value: string) => void;
  updateDemographicsAddress: (field: keyof DemographicsAddress, value: string) => void;
  // Actions — review
  setAcknowledgeTerms: (accepted: boolean) => void;
  // Actions — submit
  submitEnrollment: () => Promise<void>;
  resetWizard: () => void;
  reset: () => void;
}

// ─── None-toggle helpers ──────────────────────────────────────────────────────

/** When any non-None field (checkbox or Other text) is set, clear None if anything is now set. */
function recalcNoneAfterUpdate<T extends { none: boolean }>(profile: T): T {
  const { none: _none, ...rest } = profile as Record<string, unknown>;
  const anySet = Object.entries(rest).some(([, v]) => Boolean(v));
  return { ...profile, none: !anySet };
}

// ─── Store implementation ─────────────────────────────────────────────────────

export const useMailOrderStore = create<MailOrderState>((set, get) => ({
  // Initial state
  currentStep: 1,
  selectedUser: null,
  allergies: { ...DEFAULT_ALLERGIES },
  severity: { ...DEFAULT_SEVERITY },
  conditions: { ...DEFAULT_CONDITIONS },
  eprotectResponse: null,
  paymentType: 'card',
  cardName: '',
  cardExpMonth: '',
  cardExpYear: '',
  cardLast4: '',
  nyDisclaimerAccepted: false,
  demographics: {
    phone: { ...DEFAULT_DEMOGRAPHICS.phone },
    address: { ...DEFAULT_DEMOGRAPHICS.address },
  },
  acknowledgeTerms: false,
  isSubmitting: false,
  submitError: null,
  submitSuccess: false,

  // Navigation
  setStep: step => set({ currentStep: step }),
  nextStep: () => set(s => ({ currentStep: Math.min(s.currentStep + 1, 5) })),
  prevStep: () => set(s => ({ currentStep: Math.max(s.currentStep - 1, 1) })),

  // User selection
  setSelectedUser: user =>
  set(s => {
    if (s.selectedUser?.linkToken === user?.linkToken) return { selectedUser: user };
    return {
      selectedUser: user,
      allergies: { ...DEFAULT_ALLERGIES }, severity: { ...DEFAULT_SEVERITY },
      conditions: { ...DEFAULT_CONDITIONS }, eprotectResponse: null,
      paymentType: 'card', cardName: '', cardExpMonth: '', cardExpYear: '', cardLast4: '',
      nyDisclaimerAccepted: false,
      demographics: { phone: { ...DEFAULT_DEMOGRAPHICS.phone }, address: { ...DEFAULT_DEMOGRAPHICS.address } },
      acknowledgeTerms: false,
    };
  }),

  // Health profile — allergies
  updateAllergy: (key, value) =>
    set(s => {
      const updated = { ...s.allergies, [key]: value };
      return { allergies: recalcNoneAfterUpdate(updated) };
    }),

  setAllergyNone: () =>
    set({
      allergies: { ...DEFAULT_ALLERGIES, none: true },
    }),

  // Health profile — severity
  updateSeverity: (key, value) =>
    set(s => {
      const updated = { ...s.severity, [key]: value };
      return { severity: recalcNoneAfterUpdate(updated) };
    }),

  setSeverityNone: () =>
    set({
      severity: { ...DEFAULT_SEVERITY, none: true },
    }),

  // Health profile — conditions
  updateCondition: (key, value) =>
    set(s => {
      const updated = { ...s.conditions, [key]: value };
      return { conditions: recalcNoneAfterUpdate(updated) };
    }),

  setConditionNone: () =>
    set({
      conditions: { ...DEFAULT_CONDITIONS, none: true },
    }),

  // Payment
  setEprotectResponse: response => set({ eprotectResponse: response }),
  setPaymentType: type =>
    set({
      paymentType: type,
      // Clear card capture and disclaimer when switching away from card
      eprotectResponse: type === 'check' ? null : get().eprotectResponse,
      nyDisclaimerAccepted: type === 'check' ? false : get().nyDisclaimerAccepted,
    }),
  setCardName: name => set({ cardName: name }),
  setCardExpMonth: month => set({ cardExpMonth: month }),
  setCardExpYear: year => set({ cardExpYear: year }),
  setCardLast4: last4 => set({ cardLast4: last4 }),
  setNyDisclaimerAccepted: val => set({ nyDisclaimerAccepted: val }),

  // Demographics
  updateDemographicsPhone: (field, value) =>
    set(s => ({
      demographics: {
        ...s.demographics,
        phone: { ...s.demographics.phone, [field]: value },
      },
    })),

  updateDemographicsAddress: (field, value) =>
    set(s => ({
      demographics: {
        ...s.demographics,
        address: { ...s.demographics.address, [field]: value },
      },
    })),

  // Review
  setAcknowledgeTerms: accepted => set({ acknowledgeTerms: accepted }),

  // Submit
  submitEnrollment: async () => {
    const state = get();
    if (!state.selectedUser) return;

    set({ isSubmitting: true, submitError: null });

    try {
      const response = await activateMailOrder(state);
      if (response.StatusCode !== 'SUCCESS') {
        set({
          submitError: response.StatusMessage || 'Enrollment failed. Please try again.',
          isSubmitting: false,
        });
        return;
      }

      // Re-fetch account info to get updated IsInGuardian value
      const accountInfo = await getAccountInfo();
      const sessionUser = getSessionUser<SessionUser>();
      if (sessionUser && accountInfo) {
        setSessionUser({
          ...sessionUser,
          IsInGuardian: (accountInfo as Record<string, unknown>).IsInGuardian as boolean,
        });
      }

      set({ isSubmitting: false, submitSuccess: true });
    } catch (err) {
      let message = 'An unexpected error occurred. Please try again.';
      if (err instanceof Error) {
        message = err.message;
      } else if (
        err !== null &&
        typeof err === 'object' &&
        'name' in err &&
        (err as NormalizedApiError).name === 'ApiClientError'
      ) {
        const apiError = err as NormalizedApiError;
        // Log the full API error for DevTools diagnosis (status + backend response body)
        console.error('[ActivateMailOrder] API error:', apiError.status, apiError.data);
        message = `Enrollment failed (HTTP ${apiError.status}). Please try again.`;
      }
      set({ submitError: message, isSubmitting: false });
    }
  },

  // Reset
  resetWizard: () =>
    set({
      currentStep: 1,
      selectedUser: null,
      allergies: { ...DEFAULT_ALLERGIES },
      severity: { ...DEFAULT_SEVERITY },
      conditions: { ...DEFAULT_CONDITIONS },
      eprotectResponse: null,
      paymentType: 'card',
      cardName: '',
      cardExpMonth: '',
      cardExpYear: '',
      cardLast4: '',
      nyDisclaimerAccepted: false,
      demographics: {
        phone: { ...DEFAULT_DEMOGRAPHICS.phone },
        address: { ...DEFAULT_DEMOGRAPHICS.address },
      },
      acknowledgeTerms: false,
      isSubmitting: false,
      submitError: null,
      submitSuccess: false,
    }),

  reset: () => get().resetWizard(),
}));
